/**
 * Builds a seamless announcement marquee from the theme-editor announcement blocks.
 * The original group remains accessible; generated repetitions are presentation-only.
 */
export class AnnouncementBar extends HTMLElement {
  /** @type {ResizeObserver | undefined} */
  #resizeObserver;

  /** @type {number | undefined} */
  #resizeFrame;

  connectedCallback() {
    this.#buildTrack();
    this.#resizeObserver = new ResizeObserver(() => {
      window.cancelAnimationFrame(this.#resizeFrame);
      this.#resizeFrame = window.requestAnimationFrame(() => this.#ensureCoverage());
    });
    this.#resizeObserver.observe(this);
  }

  disconnectedCallback() {
    this.#resizeObserver?.disconnect();
    window.cancelAnimationFrame(this.#resizeFrame);
  }

  #buildTrack() {
    const track = this.querySelector('[data-marquee-track]');
    const group = this.querySelector('[data-marquee-group]');
    if (!(track instanceof HTMLElement) || !(group instanceof HTMLElement)) return;
    if (!group.querySelector('[data-marquee-item]')) return;

    track.querySelector('[data-marquee-clone]')?.remove();
    this.#fillGroup(group);

    const clone = group.cloneNode(true);
    if (!(clone instanceof HTMLElement)) return;
    clone.dataset.marqueeClone = '';
    clone.removeAttribute('data-marquee-group');
    clone.setAttribute('aria-hidden', 'true');
    this.#disableGeneratedContent(clone);
    track.append(clone);
    this.dataset.marqueeReady = '';
  }

  #ensureCoverage() {
    const group = this.querySelector('[data-marquee-group]');
    if (!(group instanceof HTMLElement)) return;
    if (group.scrollWidth >= this.clientWidth + 160) return;
    this.#buildTrack();
  }

  /** @param {HTMLElement} group */
  #fillGroup(group) {
    const seedNodes = Array.from(group.children).filter((node) => !node.hasAttribute('data-marquee-repeat'));
    if (!seedNodes.length) return;

    let pass = 0;
    while (group.scrollWidth < this.clientWidth + 160 && pass < 12) {
      for (const node of seedNodes) {
        const clone = node.cloneNode(true);
        if (!(clone instanceof HTMLElement)) continue;
        clone.dataset.marqueeRepeat = '';
        clone.setAttribute('aria-hidden', 'true');
        this.#disableGeneratedContent(clone);
        group.append(clone);
      }
      pass += 1;
    }
  }

  /** @param {HTMLElement} root */
  #disableGeneratedContent(root) {
    root.removeAttribute('id');
    root.removeAttribute('data-shopify-editor-block');
    for (const element of root.querySelectorAll('[id], [data-shopify-editor-block], a, button, input, select, textarea')) {
      element.removeAttribute('id');
      element.removeAttribute('data-shopify-editor-block');
      if (element.matches('a, button, input, select, textarea')) element.setAttribute('tabindex', '-1');
    }
  }
}

if (!customElements.get('announcement-bar-component')) {
  customElements.define('announcement-bar-component', AnnouncementBar);
}
